import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchProvider } from '../contexts/SearchContext';
import useSearch from '../contexts/useSearch';
import SearchInput from '../components/SearchInput';
import SearchResults from '../components/SearchResults';
import ItemActionButton from '../components/ItemActionButton';
import Paginated from '../components/Paginated';
import useAuth from '../contexts/useAuth';
import type { ListData } from '../../../types';

type SortMode = 'new' | 'top';

const publicationFromUrl = (sourceUrl?: string) => {
  if (!sourceUrl) return '';
  try {
    return new URL(sourceUrl).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
};

const ArticleRow = ({ item }: { item: ListData }) => {
  const navigate = useNavigate();
  const publication = publicationFromUrl(item.sourceUrl);
  const meta = [item.author, publication].filter(Boolean).join(' · ');

  return (
    <div
      onClick={() => navigate(`/id/${item.id}`)}
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: 12,
        alignItems: 'center',
        padding: '14px 10px',
        borderBottom: '1px solid var(--border)',
        cursor: 'pointer',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <h3 className="item-title" style={{ marginBottom: 4 }}>
          {item.title}
        </h3>
        {meta && <p className="subtle-text">{meta}</p>}
      </div>
      <ItemActionButton item={item} />
    </div>
  );
};

const HomeContent = () => {
  const { user } = useAuth();
  const { isSearchActive } = useSearch();
  const [sort, setSort] = useState<SortMode>('new');

  const renderArticle = useCallback(
    (item: ListData) => <ArticleRow item={item} />,
    []
  );

  return (
    <div className="page">
      <div style={{ paddingTop: 10 }}>
        <div
          style={{
            margin: '4px 10px 14px',
            padding: '18px 14px 16px',
            textAlign: 'center',
            background: 'var(--card-bg)',
            border: '2px solid var(--accent)',
            borderRadius: 8,
            boxShadow: '0 4px 18px rgba(193, 119, 103, 0.16)',
          }}
        >
          <h2
            style={{
              margin: '0 0 6px',
              fontFamily: 'var(--font-display)',
              fontSize: '1.55rem',
              fontWeight: 500,
              lineHeight: 1.15,
              color: 'var(--primary)',
            }}
          >
            Listen to anything worth reading
          </h2>
          <p
            style={{
              margin: '0 auto 14px',
              maxWidth: 420,
              fontSize: '0.98rem',
              lineHeight: 1.35,
              color: 'var(--primary-soft)',
            }}
          >
            Paste a blog post, article, or essay. Earmark turns it into audio
            you can play now and keep in your queue.
          </p>
          <SearchInput
            placeholder="Paste a URL or search articles"
            style={{
              margin: 0,
              border: '2px solid var(--accent)',
              borderRadius: 8,
              boxShadow: 'inset 0 0 0 1px rgba(193, 119, 103, 0.12)',
            }}
          />
        </div>

        {isSearchActive ? (
          <SearchResults renderResult={renderArticle} />
        ) : (
          <>
            <div
              style={{
                display: 'flex',
                padding: '0 10px 8px',
              }}
            >
              {(['new', 'top'] as const).map((mode) => (
                <button
                  key={mode}
                  className="pill-button"
                  onClick={() => setSort(mode)}
                  style={{
                    flex: 1,
                    borderRadius: 0,
                    background:
                      sort === mode ? 'var(--gold)' : 'var(--card-bg)',
                    color:
                      sort === mode ? 'var(--background)' : 'var(--primary)',
                    borderColor:
                      sort === mode ? 'var(--gold)' : 'var(--border)',
                    textTransform: 'capitalize',
                  }}
                >
                  {mode}
                </button>
              ))}
            </div>
            <Paginated<ListData>
              route={`/api/browse?sort=${sort}`}
              render={(item) => <ArticleRow key={item.id} item={item} />}
              emptyMessage={
                user ? 'No articles yet' : 'Sign in to start listening'
              }
            />
          </>
        )}
      </div>
    </div>
  );
};

export default function Home() {
  return (
    <SearchProvider>
      <HomeContent />
    </SearchProvider>
  );
}
